import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusCircle } from '../../icons';
import { Autocomplete } from '../Autocomplete';
import { getOptionLabel, getOptionSelected } from '../Autocomplete/AutocompleteSelectors';
import { useBiAction } from '../BiTracking';
import { Column } from '../Layout/Column';
import { FullRow } from '../Layout/FullRow';
import { MenuPromotionalAction } from '../Menu/MenuPromotionalAction/MenuPromotionalAction';
import { TextButton } from '../TextButton';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import styles from './EnhancedAutocomplete.module.scss';
import { EnhancedAutocompleteTestIds, EnhancedAutocompleteOptionType, getOptions, mapDataToOption } from './EnhancedAutocomplete.utils';
export const EnhancedAutocomplete = (props)=>{
    var _ref;
    const { testId, applyOptionChange: _applyOptionChange, options: _options, menuWidth, emptySearchText, newItemOption, promotionalActionOption, isLoading, onRefresh, textFieldProps, className, style, selectedOption, disabled, itemRenderer, size, error, helperText, disabledItems, onInputChange, disableClientFilter, bi, noBi: _noBi } = props;
    const logBiAction = useBiAction();
    const newItemOptions = useMemo(()=>{
        if (!newItemOption) return [];
        return Array.isArray(newItemOption) ? newItemOption : [
            newItemOption
        ];
    }, [
        newItemOption
    ]);
    const disabledItemsMap = useMemo(()=>{
        if (!disabledItems) return new Map();
        return new Map(disabledItems.map((item)=>[
                item.id,
                item.tooltip
            ]));
    }, [
        disabledItems
    ]);
    const externalCurrentOption = useMemo(()=>{
        return selectedOption ? mapDataToOption(selectedOption, props.keyField, props.labelField) : null;
    }, [
        selectedOption,
        props.keyField,
        props.labelField
    ]);
    const [managedCurrentOption, setManagedCurrentOption] = useState(externalCurrentOption);
    const [showAllOptions, setShowAllOptions] = useState(false);
    useEffect(()=>{
        if ((externalCurrentOption === null || externalCurrentOption === void 0 ? void 0 : externalCurrentOption.type) === EnhancedAutocompleteOptionType.Item) {
            setManagedCurrentOption(externalCurrentOption);
        }
    }, [
        externalCurrentOption
    ]);
    const options = useMemo(()=>{
        return getOptions(_options, props.keyField, props.labelField);
    }, [
        _options,
        props.keyField,
        props.labelField
    ]);
    const value = managedCurrentOption;
    const valueLabel = (_ref = value === null || value === void 0 ? void 0 : value.label) !== null && _ref !== void 0 ? _ref : '';
    const applyOptionChange = useCallback((newOption)=>{
        if ((bi === null || bi === void 0 ? void 0 : bi.name) && (newOption === null || newOption === void 0 ? void 0 : newOption.type) === EnhancedAutocompleteOptionType.Item) {
            logBiAction({
                entityType: 'enhanced_autocomplete',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : '',
                    selected_value: newOption.label
                })
            });
        }
        setManagedCurrentOption(newOption);
        const item = newOption === null || newOption === void 0 ? void 0 : newOption.data;
        _applyOptionChange === null || _applyOptionChange === void 0 ? void 0 : _applyOptionChange(item);
    }, [
        bi,
        logBiAction,
        testId,
        _applyOptionChange
    ]);
    return /*#__PURE__*/ _jsx(Autocomplete, {
        noBi: true,
        id: testId !== null && testId !== void 0 ? testId : EnhancedAutocompleteTestIds.EnhancedAutocomplete(),
        error: !!error,
        helperText: helperText,
        disabled: disabled,
        selectOnFocus: true,
        className: className,
        style: style,
        size: size || 'small',
        menuWidth: menuWidth,
        textFieldProps: textFieldProps,
        forcePopupIcon: true,
        testId: testId !== null && testId !== void 0 ? testId : EnhancedAutocompleteTestIds.EnhancedAutocomplete(),
        loading: isLoading,
        options: options,
        clearOnEscape: true,
        popoverFooter: (newItemOptions.length > 0 || promotionalActionOption) && /*#__PURE__*/ _jsxs(Column, {
            className: styles.popoverFooter,
            children: [
                newItemOptions.map((option, index)=>{
                    var _option_icon;
                    const button = /*#__PURE__*/ _jsx(TextButton, {
                        noBi: true,
                        className: styles.addNewItemOption,
                        testId: EnhancedAutocompleteTestIds.NewItemOption(index),
                        underline: "none",
                        prefixIcon: (_option_icon = option.icon) !== null && _option_icon !== void 0 ? _option_icon : /*#__PURE__*/ _jsx(PlusCircle, {}),
                        disabled: option.disabled,
                        tooltip: option.disabled && option.disabledTooltip ? {
                            title: option.disabledTooltip,
                            placement: 'top'
                        } : undefined,
                        onMouseDown: async ()=>{
                            if (option.disabled) return;
                            const newItem = await option.onClick();
                            await (onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh());
                            if (newItem) {
                                applyOptionChange(mapDataToOption(newItem, props.keyField, props.labelField));
                            }
                        },
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            weight: "normal",
                            skin: option.disabled ? 'disabled' : 'primary',
                            children: option.text
                        })
                    });
                    return option.disabled ? /*#__PURE__*/ _jsx("div", {
                        className: styles.addNewItemOptionWrapper,
                        onMouseDown: (e)=>e.preventDefault(),
                        children: button
                    }, index) : /*#__PURE__*/ _jsx(React.Fragment, {
                        children: button
                    }, index);
                }),
                promotionalActionOption && /*#__PURE__*/ _jsx(MenuPromotionalAction, {
                    noBi: true,
                    testId: EnhancedAutocompleteTestIds.PromotionalActionOption(),
                    title: promotionalActionOption.title,
                    body: promotionalActionOption.body,
                    onMouseDown: ()=>{
                        promotionalActionOption.onClick();
                    }
                })
            ]
        }),
        renderOption: (option)=>{
            if ((option === null || option === void 0 ? void 0 : option.type) === EnhancedAutocompleteOptionType.EmptySearch) {
                return /*#__PURE__*/ _jsx(Typography, {
                    as: "p",
                    testId: EnhancedAutocompleteTestIds.EmptySearchOption(),
                    children: option.label
                });
            }
            const tooltip = disabledItemsMap.get(option.value);
            const content = /*#__PURE__*/ _jsx(FullRow, {
                testId: EnhancedAutocompleteTestIds.EnhancedAutocompleteOption(option.value),
                children: itemRenderer ? itemRenderer(option.data) : /*#__PURE__*/ _jsx(Typography, {
                    as: "p",
                    ellipsis: true,
                    children: option.label
                })
            });
            if (tooltip) {
                return /*#__PURE__*/ _jsx(Tooltip, {
                    title: tooltip,
                    placement: "right",
                    children: /*#__PURE__*/ _jsx("span", {
                        "data-testid": EnhancedAutocompleteTestIds.EnhancedAutocompleteOptionTooltip(option.value),
                        style: {
                            display: 'block',
                            width: '100%'
                        },
                        children: content
                    })
                });
            }
            return content;
        },
        filterOptions: (options, state)=>{
            const NO_MATCHES_OPTION = {
                value: 'empty-search',
                label: emptySearchText,
                type: EnhancedAutocompleteOptionType.EmptySearch
            };
            const searchText = state.inputValue || valueLabel;
            const filteredOptions = showAllOptions || disableClientFilter ? options : options.filter((option)=>option.label.toLowerCase().includes(searchText.toLowerCase()));
            return filteredOptions.length === 0 ? [
                NO_MATCHES_OPTION
            ] : filteredOptions;
        },
        value: value,
        inputValue: valueLabel,
        getOptionLabel: getOptionLabel,
        getOptionSelected: getOptionSelected,
        onBlur: ()=>{
            if ((managedCurrentOption === null || managedCurrentOption === void 0 ? void 0 : managedCurrentOption.type) !== EnhancedAutocompleteOptionType.Item) {
                applyOptionChange(null);
            }
        },
        onOpen: ()=>{
            setShowAllOptions(true);
        },
        onInputChange: (_, value, reason)=>{
            setShowAllOptions(false);
            if (reason === 'clear') {
                applyOptionChange(null);
                onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange('');
                return;
            }
            if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
            onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(value);
            const option = options.find((s)=>s.label === value);
            const fallbackServiceOptionValue = {
                value: '',
                label: value,
                type: EnhancedAutocompleteOptionType.IncompleteSearch
            };
            applyOptionChange(option !== null && option !== void 0 ? option : fallbackServiceOptionValue);
        },
        noOptionsText: emptySearchText,
        getOptionDisabled: (option)=>option.type === EnhancedAutocompleteOptionType.EmptySearch || disabledItemsMap.has(option.value),
        onChange: async (event, value)=>{
            if (!value) {
                applyOptionChange(null);
                return;
            }
            const newValue = typeof value === 'string' ? {
                value: '',
                label: value,
                type: EnhancedAutocompleteOptionType.IncompleteSearch
            } : value;
            applyOptionChange(newValue);
        }
    });
};
